import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { cn } from '../../common/utils';
import DropdownSelector from '../../components/actions/DropdownSelector';
import * as Icon from '../../components/icons';
import useActualityDatasetTableTypes from '../hooks/useActualityDatasetTableTypes';
import { useActualityEntityCallbacks } from '../hooks/useActualityEntityCallbacks';
import { actualityDataSetTableTypeSelected } from '../store/actuality.state';
import { ActualityEntityType } from '../store/actuality.types';

export type ActualityEntityTagProps = {
  entity: ActualityEntityType;
  title: string | React.ReactNode;
  filters: Record<string, string>;
  color?: string;
  className?: string;
  iconDropMenu?: React.ReactNode;
  iconClassName?: string;
  hasIcon?: boolean;
};

const ActualityEntityTag: React.FC<ActualityEntityTagProps> = props => {
  const { t } = useTranslation('feed');
  const [selectedTableType, setSelectedTableType] = useRecoilState(
    actualityDataSetTableTypeSelected,
  );
  const { selectActualityAgent, selectActualityJob } =
    useActualityEntityCallbacks();

  const customActions: Partial<Record<ActualityEntityType, any>> = {
    job: [
      {
        id: 'action-navigate-job',
        title: t`Show Job`,
        shortTitle: t`Show Job`,
        requiredFilters: ['agentId', 'jobId'],
        onSelect: () => {
          const agentId = props.filters['agentId'];
          const jobId = props.filters['jobId'];
          if (agentId && jobId) {
            selectActualityJob(agentId, jobId);
          }
        },
      },
    ],
    agentId: [
      {
        id: 'action-navigate-agent',
        title: t`Show processes`,
        shortTitle: t`Show processes`,
        requiredFilters: ['agentId'],
        onSelect: () => {
          const agentId = props.filters['agentId'];
          if (agentId) {
            selectActualityAgent(agentId);
          }
        },
      },
    ],
  };

  const tableDescriptorsMap = useActualityDatasetTableTypes();
  const applicableTables = _(tableDescriptorsMap)
    .filter(d => d.entities.has(props.entity))
    .value();

  const applicableActions = _.filter(customActions[props.entity] ?? [], d =>
    _.every(d?.requiredFilters, f => _.has(props.filters, f)),
  );
  const applicableTablesMap = _.keyBy(applicableTables, 'id');
  const applicableActionsMap = _.keyBy(applicableActions, 'id');
  const optionsMap = _.keyBy([...applicableTables, ...applicableActions], 'id');

  const iconStyle = 'h-3 w-3 mx-1';
  const [isHovered, setIsHovered] = React.useState(false);

  function onActionSelect(actionId: string) {
    if (applicableTablesMap[actionId]) {
      setSelectedTableType(applicableTablesMap[actionId].id);
      applicableTablesMap[actionId].setFilter({
        searchValues: props.filters,
        sortValues: {},
      });
    }
    if (applicableActionsMap[actionId]) {
      applicableActionsMap[actionId].onSelect();
    }
  }

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-component="ActualityEntityTag"
      className={cn(
        'group/itemdatatag',
        'bg-menu/30 hover/itemdatatag:bg-menu/50',
        'hover/itemdatatag:text-menu-active',
        'hover/itemdatatag:shadow-xl',
        'rounded',
        'flex-1',
        'w-full',
        'text-sm',
        'flex items-center',
        'group-hover/itemdatatag:text-menu-active cursor-pointer',
        'whitespace-nowrap',
        'h-7',

        // 'truncate',
        props.className,
      )}
    >
      <div className="flex w-full items-center">
        {!_.isNil(props.color) && (
          <div
            data-component="legend"
            className={cn('h-6 w-1 flex-shrink-0 rounded-sm')}
            style={{
              backgroundColor: props.color,
            }}
          />
        )}

        <DropdownSelector
          className="flex-shrink-0 text-xs"
          classNameItem="text-xxs"
          iconDropMenuClass={classNames(
            isHovered ? 'h-4 w-4' : 'h-4 w-4',
            props.iconClassName,
          )}
          iconDropMenuClassName={classNames('!pl-2 !pr-0 !py-0.5 h-full')}
          onClick={e => {
            e.stopPropagation();
          }}
          DropAlignLeft
          buttonTransparent
          vertical
          panelMode
          iconDropMenu={
            isHovered && props.hasIcon
              ? Icon.MenuDotsHorizontal
              : (props.iconDropMenu ?? Icon.MenuDotsHorizontal)
          }
          iconSelectionClass={classNames(iconStyle)}
          value={'...'}
          values={_.keys(optionsMap)}
          renderValue={v => optionsMap[v].shortTitle}
          onChange={async (v, e) => {
            e.stopPropagation();
            onActionSelect(v);
          }}
        />

        <span
          className={cn(
            'flex-1',
            'truncate',
            'ltr:ml-1 rtl:mr-1',
            'ltr:mr-4 rtl:ml-4',
            'min-w-0',
            'py-1',
          )}
        >
          {props.title}
        </span>
      </div>
    </div>
  );
};

export default ActualityEntityTag;
